import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, GitCompare, Wand2, Target, ShieldCheck } from "lucide-react";

const Tags = ({ items }) =>
  (items || []).length ? (
    <div className="flex flex-wrap gap-1.5">
      {items.map((t, i) => <Badge key={i} variant="secondary" className="rounded-full">{typeof t === "string" ? t : t.name}</Badge>)}
    </div>
  ) : <span className="text-xs text-muted-foreground italic">—</span>;

const Exp = ({ items, bullets }) =>
  (items || []).length ? (
    <ul className="space-y-2">
      {items.map((e, i) => (
        <li key={i} className="text-sm">
          <div className="font-medium">{[e.role, e.employer].filter(Boolean).join(" · ")}</div>
          {e.period && <div className="text-xs text-muted-foreground">{e.period}</div>}
          {bullets && (e.bullets || []).length > 0 && (
            <ul className="list-disc pl-5 mt-1 text-xs text-muted-foreground space-y-0.5">
              {e.bullets.map((b, j) => <li key={j}>{b}</li>)}
            </ul>
          )}
        </li>
      ))}
    </ul>
  ) : <span className="text-xs text-muted-foreground italic">—</span>;

const Edu = ({ items }) =>
  (items || []).length ? (
    <ul className="space-y-1.5">
      {items.map((e, i) => (
        <li key={i} className="text-sm">
          <span className="font-medium">{e.name}</span>
          {e.school && <span className="text-muted-foreground"> · {e.school}</span>}
          {e.period && <span className="text-xs text-muted-foreground"> ({e.period})</span>}
        </li>
      ))}
    </ul>
  ) : <span className="text-xs text-muted-foreground italic">—</span>;

export default function CVProposalDiff({ proposal, mode, onOpenEditor, onBack }) {
  const orig = proposal.original_extracted || {};
  const prop = proposal.ai_version || proposal.edited_version || {};

  const rows = [
    {
      label: "Professionell profil",
      original: orig.headline ? <p className="text-sm">{orig.headline}</p> : <span className="text-xs text-muted-foreground italic">Fanns ej i originalet</span>,
      proposal: prop.professional_profile ? <p className="text-sm">{prop.professional_profile}</p> : <span className="text-xs text-muted-foreground italic">—</span>,
    },
    { label: "Kärnkompetenser", original: <Tags items={orig.skills} />, proposal: <Tags items={prop.core_competencies} /> },
    { label: "Arbetslivserfarenhet", original: <Exp items={orig.work_experiences} />, proposal: <Exp items={prop.work_experience} bullets /> },
    { label: "Utbildning", original: <Edu items={orig.educations} />, proposal: <Edu items={prop.education} /> },
    { label: "System och verktyg", original: <Tags items={orig.systems_tools} />, proposal: <Tags items={prop.systems_tools} /> },
    { label: "Certifikat", original: <Tags items={orig.certifications} />, proposal: <Tags items={prop.certifications} /> },
    { label: "Språk", original: <Tags items={orig.languages} />, proposal: <Tags items={prop.languages} /> },
    { label: "Personliga styrkor", original: <span className="text-xs text-muted-foreground italic">—</span>, proposal: <Tags items={prop.personal_strengths} /> },
  ];

  return (
    <div data-testid="cv-proposal-diff">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Button variant="ghost" className="rounded-full" onClick={onBack} data-testid="diff-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Tillbaka
        </Button>
        <Badge variant="secondary" className="rounded-full flex items-center gap-1.5">
          {mode === "adapt" ? <Target className="h-3.5 w-3.5" /> : <Wand2 className="h-3.5 w-3.5" />}
          {mode === "adapt" ? "Anpassat till tjänst" : "Förbättrat varsamt"}
        </Badge>
      </div>

      <PageHeader
        title="Original och förslag"
        description="Jämför ditt uppladdade CV (vänster) med AI-förslaget (höger). Alla uppgifter bygger på ditt verkliga CV – inget har lagts till. Öppna i editorn för att finjustera, spara och exportera."
      />

      <Card className="rounded-2xl border-border overflow-hidden mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 border-b border-border bg-muted/40">
          <div className="p-4 font-semibold text-sm flex items-center gap-2 border-r border-border">
            <GitCompare className="h-4 w-4 text-muted-foreground" /> Original (uppladdat)
          </div>
          <div className="p-4 font-semibold text-sm flex items-center gap-2 text-primary">
            <Wand2 className="h-4 w-4" /> Förslag från AI
          </div>
        </div>
        {rows.map((r) => (
          <div key={r.label} className="grid grid-cols-1 md:grid-cols-2 border-b border-border last:border-0">
            <div className="p-4 border-r border-border">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{r.label}</div>
              {r.original}
            </div>
            <div className="p-4 bg-primary/[0.02]">
              <div className="text-xs uppercase tracking-wide text-primary/70 mb-2 flex items-center gap-1">{r.label} <ArrowRight className="h-3 w-3" /></div>
              {r.proposal}
            </div>
          </div>
        ))}
      </Card>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-primary" /> Förslaget är sparat som ett utkast bland dina CV:n.
        </div>
        <Button className="rounded-full" onClick={onOpenEditor} data-testid="diff-open-editor-btn">
          Öppna i CV-editor <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
